import { CommonActions, createNavigationContainerRef } from '@react-navigation/native';
import { Platform } from 'react-native';

import { closeAuthOverlay, openAuthOverlay } from './authOverlayController';
import { consumeDeleteAccountReturn } from './deleteAccountReturn';
import type { RootStackParamList } from './types';
import { WEB_DELETE_ACCOUNT_PATH, WEB_HOME_PATH } from './webPaths';
import { publishWebPathname } from './webRouteStore';

export const navigationRef = createNavigationContainerRef<RootStackParamList>();

const isWeb = Platform.OS === ('web' as typeof Platform.OS);

export function navigateToAuth(): void {
  if (isWeb && openAuthOverlay()) {
    return;
  }
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Auth');
}

/** Opens the Settings stack screen (subscription, household, delete account). */
export function navigateToSettings(): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Settings');
}

/** Leave Settings if it is the current screen (e.g. before Stripe Checkout). */
export function closeSettings(): void {
  if (!navigationRef.isReady()) return;
  if (navigationRef.getCurrentRoute()?.name !== 'Settings') return;
  if (navigationRef.canGoBack()) {
    navigationRef.goBack();
    return;
  }
  navigateToHome();
}

/** Jump to Home tab (brand tap). */
export function navigateToHome(): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('MainTabs', { screen: 'Home' } as never);
}

export function navigateToLanding(): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Landing');
}

export function navigateToLegal(page: 'Privacy' | 'Terms' | 'Support' | 'DeleteAccount'): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate(page);
}

/** After login: return to /app/delete-account when that flow started sign-in; otherwise open the app. */
export function finishAuthNavigation(): void {
  closeAuthOverlay();
  if (consumeDeleteAccountReturn()) {
    if (isWeb) {
      publishWebPathname(WEB_DELETE_ACCOUNT_PATH);
    }
    if (navigationRef.isReady()) {
      navigationRef.navigate('DeleteAccount');
    }
    return;
  }
  navigateToMainTabs();
}

/** Real dashboard (signed-in). Reset so landing is not left under the stack. */
export function openDashboard(): void {
  navigateToMainTabs();
}

/** Opt-in sample dashboard — `/explore` on web. */
export function openExplore(): void {
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('Explore', { screen: 'Home' } as never);
}

/** @deprecated Guests should Sign in, not open the sample app. */
export function openWebApp(): void {
  openDashboard();
}

/** Sign in from marketing — auth first, then the real dashboard. */
export function openWebAppSignIn(): void {
  navigateToAuth();
}

export function navigateToMainTabs(): void {
  closeAuthOverlay();
  if (isWeb) {
    publishWebPathname(WEB_HOME_PATH);
  }
  if (!navigationRef.isReady()) return;
  navigationRef.dispatch(
    CommonActions.reset({
      index: 0,
      routes: [{ name: 'MainTabs' }],
    }),
  );
}

export function navigateToPreviewOnboarding(): void {
  if (isWeb) {
    navigateToHome();
    return;
  }
  if (!navigationRef.isReady()) return;
  navigationRef.navigate('PreviewOnboarding');
}

/** Pop if we came from landing; otherwise open landing. */
export function goBackOrLanding(): void {
  if (!navigationRef.isReady()) return;
  if (navigationRef.canGoBack()) {
    navigationRef.goBack();
  } else {
    navigationRef.navigate('Landing');
  }
}

export function goBackFromAuth(): void {
  if (isWeb && closeAuthOverlay()) {
    return;
  }
  goBackOrLanding();
}
